import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import { TripDetailResult } from '@/integrations/supabase/rpc-types';

interface UseTripDetailResult {
    data: TripDetailResult | null;
    loading: boolean;
    error: string | null;
    /** L'id esiste per il chiamante ma il database non lo vede: scheda non aperta. */
    notFound: boolean;
    refresh: () => Promise<void>;
}

/** Scheda del singolo viaggio, caricata all'apertura del pannello. `null` = chiuso. */
export function useTripDetail(itineraryId: string | null): UseTripDetailResult {
    const [data, setData] = useState<TripDetailResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const { t } = useI18n();

    const fetchData = useCallback(async () => {
        if (!itineraryId) {
            setData(null);
            setNotFound(false);
            return;
        }

        setLoading(true);
        setError(null);
        setNotFound(false);

        try {
            const result = await AgencyDashboardService.getTripDetail(itineraryId);
            setData(result);
            setNotFound(result === null);
        } catch {
            const msg = String(t('errors.loadFailed'));
            setError(msg);
            toast({
                title: String(t('common.error')),
                description: msg,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itineraryId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, notFound, refresh: fetchData };
}

export default useTripDetail;
